import React, { useState } from 'react';
import './Uses.css';
import { ICONS } from './sceneIcons';

// ── WHAT PEOPLE ACTUALLY USE AI FOR (Braeden 9/3) ─────────────────────────
//
//   "this is a break down of what people use ai for, which means we need to
//    have the most simple and attractive layout for these categories, than
//    break down each of these categories into more detailed sectors so it
//    niches to different people better"
//
// He sent the survey. The landing used to be a wordmark, an empty middle and
// a text box — which asks a stranger to already know what to ask for. This is
// the door instead: the eight things people really do, **in the order the
// survey says they do them**, each opening into the niches inside it.
//
//   information search 42% · work 38% · fun 25% · images & video 24% ·
//   medical 20% · diet & fitness 20% · emotional support 10% · company 4%
//
// The percentages are NOT printed. They decide the ORDER and nothing else —
// a person looking for someone to talk to does not need to be told they are
// in the 10%.
//
// Every sector sends a real typed turn (`inputMode: 'text'`, so it lands in
// the timeline like anything else he types). Nothing here is a label on a
// door that opens onto nothing.
//
// TWO THINGS THE COPY HAS TO KEEP DOING:
//   · SHE ASKS. Half of these open with "ask me…" because the house rule is
//     that she never assumes whose life this is (ARIA IS UNIVERSAL). "Find me
//     a plumber" is a search; "help me price a job" has to start by asking
//     what the job is.
//   · THE HEALTH LANE EXPLAINS, PREPARES AND FINDS — it never diagnoses.
//     Understand what you were told, know what to ask, find someone who takes
//     new patients. That is the honest version of the 20%, and it is also the
//     useful one.

const CATS = [
  {
    id: 'lookup',
    icon: 'search',
    title: 'Look something up',
    sub: 'Prices, places, people, facts',
    sectors: [
      { label: 'Find a local pro', prompt: 'Find me a good plumber near me — show ratings and prices.' },
      { label: 'Cars for sale', prompt: 'Show me used SUVs for sale near me under $25,000.' },
      { label: 'Houses & rentals', prompt: 'Show me houses for sale near me under $250,000.' },
      { label: 'Compare prices', prompt: 'Compare prices on a washer and dryer set and tell me which is the better buy.' },
      { label: 'Weather & news', prompt: "What's the weather where I am this week?" },
      { label: 'Explain something', prompt: 'Explain how a home warranty works in plain English, and whether it is usually worth it.' },
    ],
  },
  {
    id: 'work',
    icon: 'tools',
    title: 'Work',
    sub: 'Quotes, emails, plans, ads',
    sectors: [
      { label: 'Price a job', prompt: 'Help me price a job. Ask me what it involves first.' },
      { label: 'Write an email', prompt: 'Write a reply to a customer who says my price is too high. Ask me what they said first.' },
      { label: 'Ads & marketing', prompt: 'Write an ad for my business and tell me where to post it. Ask me about the business first.' },
      { label: 'Notes into a summary', prompt: 'Turn my rough notes into a clean summary I can send. Ask me for the notes.' },
      { label: 'Plan my week', prompt: 'Help me plan my week. Ask me what is on it first.' },
      { label: 'Numbers & spreadsheets', prompt: 'Help me work out my numbers for the month. Ask me what I am tracking.' },
    ],
  },
  {
    id: 'fun',
    icon: 'star',
    title: 'Something fun',
    sub: 'Trivia, stories, what to watch',
    sectors: [
      { label: 'What to watch', prompt: 'What should I watch tonight? Ask me what I am in the mood for.' },
      { label: 'Quiz me', prompt: 'Quiz me on random trivia, one question at a time. Keep score.' },
      { label: 'Tell me a story', prompt: 'Make up a short story for me. Ask me who it is for first.' },
      { label: 'Play a game', prompt: 'Play twenty questions with me. You think of something and I will guess.' },
      { label: 'Learn a hobby', prompt: 'Explain the rules of pickleball like I have never played.' },
      { label: 'Weekend ideas', prompt: 'Give me three cheap weekend trip ideas from where I live.' },
    ],
  },
  {
    id: 'make',
    icon: 'camera',
    title: 'Pictures & video',
    sub: 'Make one, fix one, restore one',
    sectors: [
      { label: 'Make an image', prompt: 'Make me an image. Ask me what I want in it first.' },
      { label: 'Fix up a photo', prompt: 'Help me fix up a photo — I will show you which one.' },
      { label: 'Photos of my work', prompt: 'Help me take better photos of my work for my listings. What should I do differently?' },
      { label: 'Logo & branding', prompt: 'Help me come up with a logo. Ask me about the business first.' },
      { label: 'A short video', prompt: 'Make me a short video for social. Ask me what it is for first.' },
      { label: 'Restore an old photo', prompt: 'Help me restore an old damaged family photo.' },
    ],
  },
  {
    id: 'health',
    icon: 'shield',
    title: 'Health questions',
    sub: 'Understand it, prepare, find someone',
    sectors: [
      { label: 'Explain what I was told', prompt: 'Help me understand what my doctor told me, in plain English. I will tell you what they said.' },
      { label: 'About a medication', prompt: 'Explain what a medication is for and what I should ask my pharmacist about it.' },
      { label: 'Find a doctor', prompt: 'Find a dentist near me that is taking new patients.' },
      { label: 'Insurance in plain English', prompt: 'Explain deductible, copay and out-of-pocket like I have never had insurance.' },
      { label: 'Prep for an appointment', prompt: 'Help me write down the questions to ask at my appointment. Ask me what it is for.' },
      { label: 'Make sense of results', prompt: 'Help me understand the numbers on my test results in plain English.' },
    ],
  },
  {
    id: 'body',
    icon: 'leaf',
    title: 'Food & fitness',
    sub: 'Meals, workouts, sleep',
    sectors: [
      { label: "What's for dinner", prompt: 'What can I make with what is in my fridge? Ask me what I have.' },
      { label: 'Meals for the week', prompt: 'Plan my meals for the week. Ask me what I like and what I avoid.' },
      { label: 'Groceries on a budget', prompt: 'Build me a grocery list for the week under $100.' },
      { label: 'Workout at home', prompt: 'Give me a 20-minute workout I can do at home with no equipment.' },
      { label: 'Sleep better', prompt: 'Help me fix my sleep schedule. Ask me what it looks like now.' },
      { label: 'Eat better, no diet', prompt: 'Help me eat better without going on a diet. Ask me what I eat in a normal day.' },
    ],
  },
  {
    id: 'talk',
    icon: 'message',
    title: 'Talk it through',
    sub: 'Decisions, hard conversations',
    sectors: [
      { label: 'Help me decide', prompt: 'Help me think through a decision. Ask me what it is.' },
      { label: 'What do I say', prompt: 'Help me figure out what to say to someone I am frustrated with. Ask me what happened.' },
      { label: 'Rough day', prompt: 'I have had a rough day. I just want to talk it out for a minute.' },
      { label: 'Money stress', prompt: 'Help me make a plan for the money side of things. Ask me what is worrying me.' },
      { label: 'Nervous about something', prompt: 'Help me get ready for something I am nervous about. Ask me what it is.' },
      { label: 'Wind down', prompt: 'Walk me through winding down before bed.' },
    ],
  },
  {
    id: 'company',
    icon: 'people',
    title: 'Company',
    sub: 'Check in, learn, pass the time',
    sectors: [
      { label: 'Check in on me', prompt: 'Check in on me — ask me how my day went.' },
      { label: 'Teach me something', prompt: 'Teach me something new today. Something I would not have looked up.' },
      { label: 'Talk about my day', prompt: 'I want to tell you about my day.' },
      { label: 'Keep me company', prompt: 'Keep me company while I work. Talk to me every so often.' },
      { label: 'Get to know me', prompt: 'Ask me some questions so you know me better, one at a time.' },
      { label: 'A word a day', prompt: 'Teach me a new word today and use it in a sentence I would actually say.' },
    ],
  },
];

function Glyph({ name }) {
  const paths = ICONS[name] || ICONS.bulb;
  return (
    <svg className="uses-ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {paths.map((d) => <path key={d} d={d} />)}
    </svg>
  );
}

export default function Uses({ onPick }) {
  const [openId, setOpenId] = useState(null);
  const open = CATS.find((c) => c.id === openId) || null;

  if (open) {
    return (
      <div className="uses uses--open">
        <div className="uses-bar">
          <button type="button" className="uses-back" onClick={() => setOpenId(null)} aria-label="Back to all categories">
            <span aria-hidden="true">←</span> All
          </button>
          <span className="uses-open-title"><Glyph name={open.icon} />{open.title}</span>
        </div>
        <div className="uses-sectors">
          {open.sectors.map((s) => (
            <button
              key={s.label}
              type="button"
              className="uses-sector"
              title={s.prompt}
              onClick={() => onPick(s.prompt)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="uses">
      <div className="uses-grid">
        {CATS.map((c) => (
          <button
            key={c.id}
            type="button"
            className="uses-tile"
            onClick={() => setOpenId(c.id)}
            aria-expanded="false"
          >
            <Glyph name={c.icon} />
            <span className="uses-title">{c.title}</span>
            <span className="uses-sub">{c.sub}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export { CATS };
